import React from "react";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { useSearchModalContext } from "contexts/SearchModalContext";
import { useDebounce } from "hooks/use-debounce";
import { useDebounceValue } from "hooks/use-debounce-value";
import { useUrlParams } from "hooks/use-url-params";
import { useConfigure, useSearchBox } from "react-instantsearch-hooks-web";

import { Button } from "components/Button/Button";
import { FaIcon } from "components/FaIcon/FaIcon";

import { isBot } from "utils/is-bot";

import "./SearchInput.scss";

// If the search query includes any of these terms, return empty results
// This list includes strings used in xss detectors that spam our search
// (Keep this in sync with bifrost's method on InstantSearchContext)
const BLOCKLISTED_TERMS = ["3cwas"];

const SEARCH_TERM_DEBOUNCE = 300;

export function SearchInput() {
    const { refine } = useSearchBox();
    const { state, dispatch } = useSearchModalContext();
    const { setUrlParams } = useUrlParams();

    const setNotTypingDebounced = useDebounce(() => {
        dispatch({ type: "set_is_typing", value: false });

        // must be longer debounce than SEARCH_TERM_DEBOUNCE to ensure we don't
        // see a flash of current results before new results are shown
    }, SEARCH_TERM_DEBOUNCE + 200);

    // 1 result per distinct field
    useConfigure({ distinct: 1 });

    React.useEffect(() => {
        if (state.shouldPopulateQueryFromUrl) {
            const urlParams = new URLSearchParams(window.location.search);
            const searchQuery = urlParams.get("query");

            if (searchQuery) {
                try {
                    const searchTerm = decodeURI(searchQuery);
                    dispatch({ type: "set_search_term", value: searchTerm });
                } catch (e) {
                    console.error("Error parsing search query");
                }
            }

            dispatch({
                type: "set_should_populate_query_from_url",
                value: false,
            });
        }
    }, [state.shouldPopulateQueryFromUrl, dispatch]);

    // this will only update if searchTerm hasn't changed in the last
    // SEARCH_TERM_DEBOUNCE ms
    const debouncedSearchTerm = useDebounceValue<string>(
        state.searchTerm,
        SEARCH_TERM_DEBOUNCE
    );

    React.useEffect(() => {
        setUrlParams({ query: debouncedSearchTerm });
    }, [setUrlParams, debouncedSearchTerm]);

    React.useEffect(() => {
        if (!state.searchModalOpen) {
            // reset to default
            dispatch({ type: "set_search_term", value: "" });
        }
    }, [state.searchModalOpen, dispatch]);

    // when ever typed value changes, update algolia search term
    React.useEffect(() => {
        const isBlockedTerm = BLOCKLISTED_TERMS.some((blockedTerm) =>
            debouncedSearchTerm.includes(blockedTerm)
        );

        const isBlocked = isBlockedTerm || isBot();

        if (!isBlocked) {
            refine(debouncedSearchTerm);
        }
    }, [refine, debouncedSearchTerm]);

    const handleSearchInputChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch({ type: "set_search_term", value: event.target.value });
        dispatch({ type: "set_is_typing", value: true });
        setNotTypingDebounced();
    };

    const handleSearchClear = () => {
        dispatch({ type: "set_search_term", value: "" });
    };

    // const disabled = state.searchStatus === "blocked";

    return (
        <div className="search-bar-container">
            <input
                autoFocus
                type="text"
                className="search-input"
                placeholder="Search documentation..."
                // disabled={disabled}
                value={state.searchTerm}
                onChange={handleSearchInputChange}
            />
            <div className="clear-button-container">
                <Button
                    className="clear-button"
                    variation="ghost-primary"
                    size="sm"
                    aria-label="Clear input"
                    onClick={handleSearchClear}
                >
                    <FaIcon icon={faXmark} />
                </Button>
            </div>
        </div>
    );
}
